import "./UsersDirectory.scss";
import Table from 'components/Table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import magnifier from 'assets/images/icons/magnifier.svg';
import plus from 'assets/images/icons/plus.svg';
import editIcon from 'assets/images/icons/edit.svg';
import Input from 'components/Input/Input';
import { Button } from 'components/Button/Button';
import { User } from 'services/auth/auth.types';
import { useUsersDirectory } from './useUsersDirectoryVM';
import { Switch } from 'components/Switch/Switch';
import { useSelector } from 'react-redux';
import { Modal } from 'components/Modal/Modal';
import { Formik } from 'formik';
import { UpdateUserPayload } from 'services/user/user.types';
import { Datalist } from 'components/Datalist/Datalist';
import { NewUserFormSchema } from './NewUserFormSchema';
import { getDatalists } from 'features/datalist/datalist.slice';
import { useNavigate } from 'react-router-dom';
import { MultiSelect } from "components/MultiSelect/MultiSelect";

export const UsersDirectory: React.FunctionComponent = () => {
    const { datalists } = useSelector(getDatalists);
    const navigate = useNavigate();
    const {
        users,
        search,
        setSearch,
        handleUpdateUser,
        handleUserStatusChange,
        isCreateUserModalOpen,
        setIsCreateUserModalOpen,
        handleCreateUser,
        modalMode,
        setModalMode,
        selectedUser,
        setSelectedUser,
        isGetAllUsersLoading
    } = useUsersDirectory();

    const columnHelper = createColumnHelper<User>();

    const handleModalClose = () => {
        setIsCreateUserModalOpen(false);
        setSelectedUser(null);
        setModalMode('create');
    };

    const tableColumns = [
        columnHelper.accessor('fullname', {
            header: 'Nom complet',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('email', {
            header: 'Email',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('phone', {
            header: 'Téléphone',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('role', {
            header: 'Rôle',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('departments', {
            header: 'Départements',
            cell: (info) => <i>{info.getValue().map((department: string) => datalists?.departments?.[department] || '').join(', ')}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('online', {
            header: 'Actif ?',
            cell: ({ row: { original: currRow } }) => (
                <Switch checked={currRow.online} onChange={() => {
                    handleUserStatusChange(currRow.id);
                }} disabled={false} />
            ),
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('created_at', {
            header: 'Compte créé le',
            cell: (info) => <i>{moment(info.getValue()).format('DD/MM/YYYY')}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.display({
            header: 'Actions',
            cell: ({ row: { original: currRow } }) => {
                return (
                    <span style={{ display: 'flex', columnGap: 12 }}>
                        <img
                            src={editIcon}
                            alt="edit-icon"
                            onClick={() => navigate(`/app/admin/settings/users/${currRow.id}`)}
                            style={{ cursor: 'pointer' }}
                        />
                    </span>
                );
            }
        })
    ];

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };

    return (
        <>
            <main id="users-list">
                <div className="top">
                    <Input
                        placeholder="Chercher un utilisateur"
                        name="search-person"
                        className="search-person search-bar"
                        icon={<img src={magnifier} alt="" />}
                        onChange={handleSearch}
                        value={search ? search : ''}
                    />
                    <Button
                        label="Ajouter un utilisateur"
                        variant="light"
                        icon={<img src={plus} alt="" />}
                        iconBefore
                        callback={() => setIsCreateUserModalOpen(true)}
                    />
                </div>
                <Table data={users} search={search} columns={tableColumns} columnHelper={columnHelper} className="table-card" isLoading={isGetAllUsersLoading} />
            </main>
            <Modal
                open={isCreateUserModalOpen}
                setClose={handleModalClose}
                title={modalMode === 'create' ? 'Ajouter un utilisateur' : "Modifier l'utilisateur"}
                yesText={modalMode === 'create' ? 'Ajouter' : 'Modifier'}
                noText="Annuler"
                onClose={handleModalClose}
                formId="userForm"
                isValidateSubmit
                onValidate={() => {
                    document.getElementById('userForm')?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                }}
            >
                <Formik
                    initialValues={{
                        firstname: selectedUser?.firstname || '',
                        lastname: selectedUser?.lastname || '',
                        email: selectedUser?.email || '',
                        phone: selectedUser?.phone || '',
                        role: selectedUser?.role || '',
                        departments: selectedUser?.departments || [],
                        id: selectedUser?.id || '',
                        color: selectedUser?.color || '',
                        online: selectedUser?.online || false
                    }}
                    enableReinitialize={true}
                    validationSchema={NewUserFormSchema(datalists || {})}
                    onSubmit={async (values, { setSubmitting }) => {
                        if (modalMode === 'create') {
                            handleCreateUser(values);
                        } else if (modalMode === 'edit' && selectedUser) {
                            handleUpdateUser({
                                ...values,
                                id: Number(values.id)
                            } as UpdateUserPayload);
                        }
                        setSubmitting(false);
                        handleModalClose();
                    }}
                >
                    {(props) => (
                        <form id="userForm" onSubmit={props.handleSubmit} noValidate>
                            <div className="form">
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Prénom</label>
                                        <input
                                            type="text"
                                            name="firstname"
                                            placeholder="Prénom"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.firstname}
                                        />
                                        {props.errors.firstname && props.touched.firstname && (
                                            <p className="error">{String(props.errors.firstname)}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Nom</label>
                                        <input
                                            type="text"
                                            name="lastname"
                                            placeholder="Nom"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.lastname}
                                        />
                                        {props.errors.lastname && props.touched.lastname && (
                                            <p className="error">{String(props.errors.lastname)}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Email</label>
                                        <input
                                            type="text"
                                            name="email"
                                            placeholder="Email"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.email}
                                        />
                                        {props.errors.email && props.touched.email && <p className="error">{String(props.errors.email)}</p>}
                                    </div>
                                </div>
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Téléphone</label>
                                        <input
                                            type="text"
                                            name="phone"
                                            placeholder="Téléphone"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.phone}
                                        />
                                        {props.errors.phone && props.touched.phone && <p className="error">{String(props.errors.phone)}</p>}
                                    </div>
                                </div>
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Départements</label>
                                        <MultiSelect
                                            name="departments"
                                            isMulti={true}
                                            placeholder="Sélectionner les départements"
                                            values={props.values.departments}
                                            update={(e: { label: string; value: string }[]) => {
                                                props.setFieldValue('departments', e.map((item) => item.value));
                                            }}
                                            labelKey="label"
                                            options={Object.entries(datalists?.departments || {}).map(([code, label]) => ({
                                                label: label,
                                                value: code
                                            }))}
                                            colors={[]}
                                        />
                                        {props.errors.departments && props.touched.departments && (
                                            <p className="error">{String(props.errors.departments)}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Rôle</label>
                                        <Datalist
                                            list="roles"
                                            name="role"
                                            placeholder="Sélectionner un rôle"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.role}
                                        />
                                        {props.errors.role && props.touched.role && <p className="error">{String(props.errors.role)}</p>}
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                </Formik>
            </Modal>
        </>
    );
};
